import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { ProductListItem, ProductStatusType } from '@/api/admin-products-types'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { Thumbnail } from '@/components/base/thumbnail'
import { useI18n } from '@/i18n/i18n-react'

export const useProductListColumnDefs = () => {
	const { L } = useI18n()
	return useMemo<ColDef<ProductListItem>[]>(
		() => [
			// { type: 'image', field: 'imageUrl' },
			{
				headerName: L.Fields.Name(),
				field: 'title',
				flex: 2,
				cellRenderer: ({ data }) => (
					<div className='flex h-full w-full max-w-[250px] items-center gap-x-3 overflow-hidden'>
						<div className='w-fit flex-shrink-0'>
							<Thumbnail src={data.imageUrl} />
						</div>
						<span title={data.title} className='truncate'>
							{data.title}
						</span>
					</div>
				)
			},
			{
				headerName: L.Products.Column.PricingMethod(),
				field: 'pricingMethod',
				flex: 1,
				valueGetter: ({ data }) => {
					if (!data?.pricingMethod) {
						return ''
					}
					return data.pricingMethod.map(x => L.Enum.PricingMethod(x))
				}
			},
			{
				headerName: L.Fields.SalesChannel(),
				field: 'salesChannels',
				type: 'badge',
				flex: 1,
				valueGetter: ({ data }) => data?.salesChannels ?? []
			},
			{
				headerName: L.Fields.Status(),
				field: 'status',
				flex: 1,
				cellRenderer: ({ value }: { value: ProductStatusType }) => (
					<StatusCell color={getProductStatusColor(value)}>{L.Enum.ProductStatus(value)}</StatusCell>
				)
			}
		],
		[L]
	)
}

const getProductStatusColor = (status: ProductStatusType): StatusColor => {
	switch (status) {
		case 'PUBLISHED':
			return 'green'
		case 'DRAFT':
			return 'blue'
		case 'ARCHIVED':
			return 'grey'
		default:
			return 'grey'
	}
}
